import { useState } from "react";
import { DownloadSimpleIcon } from "@phosphor-icons/react/DownloadSimple";
import { TagIcon } from "@phosphor-icons/react/Tag";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { KeyValueList } from "@freightsimple/ui-core/Display/KeyValueList";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { toast } from "sonner";
import type { OpportunityDetail } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityDetail";
import { carriersApi } from "@/App/Auth/carrierApi";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { describeCoverage } from "@/App/Views/opportunityFormatting";
import { OpportunityBreakdownPanel } from "@/App/Opportunities/OpportunityBreakdownPanel";
import { OpportunityLaneTablePanel } from "@/App/Opportunities/OpportunityLaneTablePanel";
import { downloadBase64File } from "@/App/Opportunities/downloadBase64File";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";

/**
 * One pricing opportunity, in full.
 *
 * A real page rather than a dialog: the opportunity email links straight here,
 * and the full package is far more content than a dialog should hold. The page
 * has two states — before registering interest it shows the summary and the
 * button, after it shows everything.
 *
 * It characterises the freight and prices none of it. There is no target rate
 * anywhere on this page by design.
 */
export function OpportunityPage({ companyId }: { companyId: string }) {
  const { state, refreshing, refresh } = useCarrierFetch(
    () => carriersApi().getCarrierOpportunity({ companyId }),
    companyId,
  );

  const detail = state.status === "loaded" ? state.data : undefined;
  const summary = detail?.summary;
  const unlocked = summary?.packageUnlocked === true;

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={TagIcon}
      title={summary?.companyName ?? "Opportunity"}
      description={summary?.narrative}
      refreshing={refreshing}
      headerStats={
        summary
          ? [
              { label: "Shipments", value: `${summary.shipmentsPerYear}/yr` },
              {
                label: "Your coverage",
                value:
                  summary.coveragePercentage === undefined
                    ? "—"
                    : formatPercent(summary.coveragePercentage),
              },
            ]
          : undefined
      }
    >
      {state.status === "loading" && (
        <T size="sm" color="secondary">
          Loading…
        </T>
      )}
      {state.status === "error" && (
        <CarrierLoadFailed what="this opportunity" onRetry={refresh} />
      )}
      {detail && summary && (
        <Stack gap={4}>
          <Overview_ detail={detail} />
          {unlocked ? (
            <FullPackage_ companyId={companyId} />
          ) : (
            <RegisterInterest_
              companyId={companyId}
              companyName={summary.companyName}
              onRegistered={refresh}
            />
          )}
        </Stack>
      )}
    </Page>
  );
}

function Overview_({ detail }: { detail: OpportunityDetail }) {
  const summary = detail.summary;
  const items = [
    {
      label: "Shipments",
      value: `${summary.shipmentsPerYear.toLocaleString("en-CA")} a year`,
    },
    {
      label: "Customer's freight spend",
      value:
        summary.estimatedAnnualFreightSpendCad === undefined
          ? "Not stated"
          : `${formatMoney(summary.estimatedAnnualFreightSpendCad)} a year`,
    },
    {
      label: "Your service area",
      value: describeCoverage(summary.coveragePercentage),
    },
    {
      label: "Data window",
      value: `${detail.dataWindow.fromDate} – ${detail.dataWindow.toDate} (${detail.dataWindow.shipmentCount.toLocaleString("en-CA")} shipments)`,
    },
  ];

  return (
    <Panel title="The opportunity">
      <Stack gap={3}>
        <KeyValueList items={items} />
        {summary.alreadyQuoting && (
          <Row gap={2} wrap>
            <Badge
              label={`You quote ${summary.quotedShipments?.toLocaleString("en-CA") ?? "0"} of their shipments`}
              variant="info"
            />
            {summary.winRate !== undefined && (
              <Badge
                label={`You win ${formatPercent(summary.winRate)} of them`}
                variant={summary.winRate > 0 ? "success" : "warning"}
              />
            )}
          </Row>
        )}
      </Stack>
    </Panel>
  );
}

function RegisterInterest_({
  companyId,
  companyName,
  onRegistered,
}: {
  companyId: string;
  companyName: string;
  onRegistered: () => void;
}) {
  const [registering, setRegistering] = useState(false);

  async function markInterested() {
    setRegistering(true);
    try {
      await carriersApi().markCarrierOpportunityInterested({ companyId });
      toast.success(`Thanks — we'll be in touch about ${companyName}`);
      onRegistered();
    } catch {
      toast.error("Couldn't register your interest. Please try again.");
    } finally {
      setRegistering(false);
    }
  }

  return (
    <Banner variant="info" title="Want to price this freight?">
      <Stack gap={3}>
        <T size="sm">
          Tell us you're interested and we'll open the full package straight
          away — every lane, the weight and density profile, when it moves
          through the year, and the shipment-level datafile. No waiting on us.
        </T>
        <Row>
          <Button
            primary
            onClick={markInterested}
            loading={registering}
            data-testid="opportunity-mark-interested-button"
          >
            I'm interested
          </Button>
        </Row>
      </Stack>
    </Banner>
  );
}

function FullPackage_({ companyId }: { companyId: string }) {
  const { state, refresh } = useCarrierFetch(
    () => carriersApi().getCarrierOpportunityPackage({ companyId }),
    companyId,
  );

  if (state.status === "loading") {
    return (
      <T size="sm" color="secondary">
        Loading the full package…
      </T>
    );
  }
  if (state.status === "error") {
    return <CarrierLoadFailed what="the full package" onRetry={refresh} />;
  }

  const fullPackage = state.data;

  return (
    <Stack gap={4}>
      <DatafilePanel_ companyId={companyId} fullPackage={fullPackage} />
      <OpportunityLaneTablePanel lanes={fullPackage.lanes} />
      <Grid columns={2} gap="lg">
        <OpportunityBreakdownPanel
          title="Where it ships from"
          description="Every origin over the data window."
          header="Origin"
          rows={fullPackage.pickupLocations}
          emptyMessage="No pickup locations recorded."
        />
        <OpportunityBreakdownPanel
          title="When it moves"
          description="Volume by month — freight that arrives in two months of the year is a different commitment from the same volume spread evenly."
          header="Month"
          rows={fullPackage.monthlyVolume}
          emptyMessage="No monthly volume recorded."
        />
        <OpportunityBreakdownPanel
          title="How heavy"
          description="Shipment weight bands."
          header="Weight"
          rows={fullPackage.weightBands}
          emptyMessage="No weights recorded."
        />
        <OpportunityBreakdownPanel
          title="How dense"
          description="Density drives the class, and therefore the price."
          header="Density"
          rows={fullPackage.densityBands}
          emptyMessage="No densities recorded."
        />
      </Grid>
      {fullPackage.freightProfile.accessorials.length > 0 && (
        <OpportunityBreakdownPanel
          title="Accessorials"
          description="What this freight needs beyond a plain dock-to-dock move."
          header="Accessorial"
          rows={fullPackage.freightProfile.accessorials}
          emptyMessage="No accessorials recorded."
        />
      )}
    </Stack>
  );
}

function DatafilePanel_({
  companyId,
  fullPackage,
}: {
  companyId: string;
  fullPackage: { datafileAvailable: boolean; datafileName?: string };
}) {
  const [downloading, setDownloading] = useState(false);

  async function download() {
    setDownloading(true);
    try {
      const file = await carriersApi().downloadCarrierOpportunityDatafile({
        companyId,
      });
      downloadBase64File(file.fileName, file.contentType, file.base64Content);
    } catch {
      toast.error("Couldn't download the datafile. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Panel
      title="The datafile"
      description="The shipment-level file to price from."
      headerActions={
        fullPackage.datafileAvailable ? (
          <Button
            icon={DownloadSimpleIcon}
            onClick={download}
            loading={downloading}
            data-testid="download-datafile-button"
          >
            Download
          </Button>
        ) : undefined
      }
    >
      {fullPackage.datafileAvailable ? (
        <Stack gap={2}>
          <T size="sm" color="secondary">
            {fullPackage.datafileName}
          </T>
          {/* Carriers asked what the target rate covers on five separate
              threads. Saying it here, next to the download, ends the question. */}
          <T size="sm" color="secondary">
            Target rates in the datafile are all-in — freight, fuel surcharge,
            accessorials and carbon surcharges. Only GST/HST is excluded.
          </T>
        </Stack>
      ) : (
        <T size="sm" color="secondary">
          We're still putting the datafile together for this customer.
          Everything else on this page is ready now.
        </T>
      )}
    </Panel>
  );
}
